import { computed, ComputedRef } from 'vue';
import { useStore } from 'vuex';

import { RoleNameEnum } from '@/bundles/Roles/types';

interface IUseUserRoles {
  isAdmin: ComputedRef<boolean>;
  isSuperAdmin: ComputedRef<boolean>;
  isMarketingAdmin: ComputedRef<boolean>;
  isFinancialAdmin: ComputedRef<boolean>;
  isFinancialOrMarketingAdmin: ComputedRef<boolean>;
  isClient: ComputedRef<boolean>;
}

/**
 * Role flags of the signed-in user, read from `store.state.userRoles`. A super
 * admin implies every admin flag.
 */
export const useUserRoles = (): IUseUserRoles => {
  const store = useStore();

  const hasRole = (role: RoleNameEnum): boolean => store.state.userRoles.includes(role);

  const isSuperAdmin = computed<boolean>(() => hasRole(RoleNameEnum.SUPER_ADMIN));
  const isAdmin = computed<boolean>(() => hasRole(RoleNameEnum.ADMIN) || isSuperAdmin.value);
  const isMarketingAdmin = computed<boolean>(() => hasRole(RoleNameEnum.MARKETING_ADMIN) || isSuperAdmin.value);
  const isFinancialAdmin = computed<boolean>(() => hasRole(RoleNameEnum.FINANCIAL_ADMIN) || isSuperAdmin.value);
  const isFinancialOrMarketingAdmin = computed<boolean>(() => isFinancialAdmin.value || isMarketingAdmin.value);
  const isClient = computed<boolean>(() => hasRole(RoleNameEnum.CLIENT));

  return { isAdmin, isSuperAdmin, isMarketingAdmin, isFinancialAdmin, isFinancialOrMarketingAdmin, isClient };
};
